import { useEffect, useState, useCallback } from "react";
import Button from "../../components/button";
import Input from "../../components/input";
import Chip from "../../components/chip";
import Select from "../../components/select";
import { Api } from "../../services/Api";
import { platformEvents } from "../../services/eventbus/platformEvents";
import { useNavigate } from "react-router-dom";
import { Category } from "../../types/Category";
import { Tag } from "../../types/Tag";

export default function Account() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [thinglinkUrl, setThinglinkUrl] = useState<string>("");
  const [tags, setTags] = useState<[number, string][]>([]);
  const [selectedTags, setSelectedTags] = useState<[number, string][]>([]);
  const [categories, setCategories] = useState<[number, string][]>([]);
  const [selectedCategories, setSelectedCategories] = useState<[number, string][]>([]);
  const [pathStrings, setPathStrings] = useState<string[]>([]);
  const [tagStrings, setTagStrings] = useState<string[]>([]);

  const handleCategoryClick = (name: string) => {
    const category = categories.find(([, categoryName]) => categoryName === name);
    if (category) {
      if (selectedCategories.includes(category)) {
        return;
      }
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const handleRemoveCategory = (category: [number, string]) => {
    setSelectedCategories(selectedCategories.filter((c) => c !== category));
  };

  const handleTagClick = (name: string) => {
    const tag = tags.find(([, tagName]) => tagName === name);
    if (tag) {
      if (selectedTags.includes(tag)) {
        return;
      }
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tag: [number, string]) => {
    setSelectedTags(selectedTags.filter((c) => c !== tag));
  };

  const flattenCategories = useCallback(
    (categories: Category[], path: string = ""): [number, string][] => {
      const result: [number, string][] = [];

      categories.forEach((category) => {
        const currentPath =
          path === "" ? category.name : `${path} > ${category.name}`;
        result.push([category.id, currentPath]);
        result.push(...flattenCategories(category.all_children, currentPath));
      });

      return result;
    },
    [],
  );

  useEffect(() => {
    Api("/browse/categories").then((data) => {
      setCategories(flattenCategories(data));
    });
    Api("/browse/tags").then((data: Array<Tag>) => {
      setTags(data.map((tag) => [tag.id, tag.tag]));
    });
  }, [flattenCategories]);

  useEffect(() => {
    setPathStrings(categories.map(([, path]) => path));
    setTagStrings(tags.map(([, tag]) => tag));
  }, [categories, tags]);

  const handleSubmit = () => {
    setLoading(true);
    Api("/users/content/generate/thinglink", {
      method: "POST",
      data: {
        url: thinglinkUrl,
        categories: selectedCategories.map(([id, _]) => id),
        tags: selectedTags.map(([id, _]) => id),
      },
    })
      .then(() => {
        platformEvents.emit("generalSuccess", "Content imported successfully");
        setLoading(false);
        navigate(`/content`);
      })
      .catch((data) => {
        if (data.message) {
          platformEvents.emit("generalFailure", data.message);
        } else if (data.error) {
          platformEvents.emit("generalFailure", data.error);
        }
        setLoading(false);
      });
  };



  return (
    <div className="mx-auto w-full max-w-3xl space-y-lg px-md py-lg">
      <h1 className="text-xl">Import Content</h1>
      <div className="space-y-lg">
        <p className="text-slate-100">
          You can import public content from ThingLink quickly and easily by just entering the public URL of the scene you wish to import into the box below. Please note the following:
          <ul className="list-disc pt-md pl-lg font-normal">
            <li>Only public content can be imported.</li>
            <li>Any connected scenes using transitions will not be imported.</li>
            <li>Hotspots cannot be viewed or changed using the internal hotspot editor. If you need to edit hotspots, you will need to do so using ThingLink.</li>
            <li>If you change your scene using ThingLink then you will need to delete and then re-import your scene for your changes to be effective.</li>
            <li>Video scenes are not supported at the moment due to file type restrictions, however we are working on adding this feature in the future.</li>
          </ul>
        </p>
        <div className="space-y-sm">
          <label htmlFor="name" className="block">
            ThingLink URL
          </label>
          <Input
            type="text"
            id="thinglink-url"
            placeholder="https://www.thinglink.com/scene/1234567890"
            value={thinglinkUrl}
            onChange={(e) => setThinglinkUrl(e.target.value)}
          />
        </div>

        <div className="space-y-md">
          <div className="space-y-sm">
            <div className="flex items-center gap-sm">
              <span className="h-xs w-xs rounded-full bg-purple-100"></span>
              <label htmlFor="categories">Categories (required)</label>
            </div>
            <div className="relative">
              <Select
                block
                options={pathStrings}
                value="Select categories"
                valueChanged={handleCategoryClick}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-sm">
            {selectedCategories.map((category) => (
              <Chip
                key={"category-" + category}
                colour="secondary"
                text={category[1]}
                onClick={() => handleRemoveCategory(category)}
              />
            ))}
          </div>
        </div>

        <div className="space-y-md">
          <div className="space-y-sm">
            <div className="flex items-center gap-sm">
              <span className="h-xs w-xs rounded-full bg-purple-100"></span>
              <label htmlFor="tags">Tags (required)</label>
            </div>
            <div className="relative">
              <Select
                block
                options={tagStrings}
                value="Select tags"
                valueChanged={handleTagClick}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-sm">
            {selectedTags.map((tag) => (
              <Chip
                key={"tag-" + tag}
                colour="secondary"
                text={tag[1]}
                onClick={() => handleRemoveTag(tag)}
              />
            ))}
          </div>
        </div>

        <Button 
          onClick={handleSubmit} 
          primary 
          disabled={loading || !thinglinkUrl || selectedCategories.length === 0 || selectedTags.length === 0}
        >
          {loading ? "Loading..." : "Import"}
        </Button>
      </div>
    </div>
  );
}
